import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { AlertCircle, Mic, TrendingUp, Users, Target } from "lucide-react";
import BRAND from "../brand";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Left Side - Premium Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy-gradient text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3B] via-[#0B1F3B] to-[#1e3a5f] opacity-95"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#14B8A6]/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-2">
            <img 
              src={BRAND.logoUrl} 
              alt="Revyniqx" 
              className="h-10 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="mt-4">
            <h1 className="text-3xl font-bold tracking-tight">{BRAND.productName}</h1>
            <p className="text-blue-200/80 text-sm mt-1">{BRAND.productTagline}</p>
          </div>
        </div>
        
        <div className="relative z-10 space-y-10">
          <div>
            <h2 className="text-4xl font-bold leading-tight mb-4">
              {BRAND.positioning.headline}
            </h2>
            <p className="text-blue-100/80 text-lg max-w-md">
              {BRAND.positioning.description}
            </p>
          </div>
          
          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-10 h-10 bg-[#14B8A6]/20 rounded-lg flex items-center justify-center">
                <Mic className="w-5 h-5 text-[#14B8A6]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Voice-First</h3>
                <p className="text-xs text-blue-200/60">Natural conversation practice</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-10 h-10 bg-[#2563EB]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#2563EB]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">AI Coaching</h3>
                <p className="text-xs text-blue-200/60">Expert-level feedback</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-10 h-10 bg-[#F59E0B]/20 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Readiness Scores</h3>
                <p className="text-xs text-blue-200/60">Know when you're ready</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl backdrop-blur-sm">
              <div className="w-10 h-10 bg-[#14B8A6]/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#14B8A6]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Team Insights</h3>
                <p className="text-xs text-blue-200/60">Manager dashboards</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 space-y-2">
          <p className="text-sm text-blue-200/50">{BRAND.poweredBy}</p>
          <p className="text-xs text-blue-200/30">{BRAND.companyAttribution}</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src={BRAND.logoUrl} 
              alt="Revyniqx" 
              className="h-8 w-auto mb-3"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-xl font-bold text-[#0B1F3B]">{BRAND.productName}</h1>
            <p className="text-sm text-slate-500">{BRAND.productTagline}</p>
          </div>

          <Card className="shadow-xl border-slate-200/60 rounded-2xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-[#0B1F3B]">Welcome back</CardTitle>
              <CardDescription className="text-slate-500">Sign in to continue your practice</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]"
                    data-testid="login-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 rounded-xl btn-premium" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500">
                Don't have an account?{" "}
                <Link to="/register" className="text-[#2563EB] hover:underline font-medium" data-testid="register-link">
                  Create account
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Footer for mobile */}
          <div className="lg:hidden mt-8 text-center space-y-1">
            <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
            <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
